import { useEffect, useRef, useState } from "react";

import Image from "common/components/Image";
import mailboxWithBee from "common/images/mailbox-with-bee.svg";
import { deepCombineErrors } from "common/utils/errors";
import { UnsubscribeReason as UnsubscribeReasonEnum } from "common/types";

import Button from "client/common/components/buttons/Button";
import { useClinic } from "client/clinics/hooks";
import Form from "client/common/components/forms/Form";
import Alert from "client/common/components/content/Alert";
import SelectPanel from "client/common/components/inputs/SelectPanel";
import SelectButton from "client/common/components/buttons/SelectButton";
import TextArea from "client/common/components/inputs/TextArea";

import { useMarketingEmailUnsubscribeUpdateMutation } from "../hooks";
import { UnsubscribeReasonOption } from "../types";
import { unsubscribeReasonOptions } from "../utils";

import styles from "../styles/UnsubscribeReason.module.less";

const UnsubscribeReason = ({ unsubscribeId }: { unsubscribeId: number }) => {
  const {
    mutate: updateUnsubscribe,
    isPending: isUpdating,
    error,
    isSuccess,
  } = useMarketingEmailUnsubscribeUpdateMutation({
    unsubscribeId,
  });
  const { display_name: displayName } = useClinic();
  const reasonOtherRef = useRef<HTMLTextAreaElement>(null);

  const [reason, setReason] = useState<UnsubscribeReasonOption | null>(null);
  const [reasonOther, setReasonOther] = useState<string | undefined>(undefined);

  const isReasonOther = reason?.value === UnsubscribeReasonEnum.Other;
  const deepErrors = deepCombineErrors(error?.errors);
  const detailErrors = [...(error?.detail ? [error.detail] : [])];
  // deep errors (non field errors, field errors) or error.detail (for 404)
  const errorDisplay = deepErrors.length ? deepErrors : detailErrors;

  useEffect(() => {
    if (isReasonOther) {
      reasonOtherRef.current?.focus();
    } else {
      setReasonOther(undefined);
    }
  }, [isReasonOther]);

  const canSubmit = !!(reason && (!isReasonOther || reasonOther) && !isSuccess);

  return (
    <Form
      canSubmit={canSubmit}
      onSubmit={() => updateUnsubscribe({ reason: reason?.value, reason_other: reasonOther })}
      className={styles.container}
    >
      <Image src={mailboxWithBee} alt="mailbox with bee" />
      <div className={styles.content}>
        <h1 className={styles.title}>Unsubscribe successful</h1>
        <p className={styles.description}>
          You will no longer receive email marketing from {displayName}. Transactional emails such
          as order and prescription notifications will still be sent to your email address.
        </p>
      </div>
      {!!errorDisplay.length && (
        <Alert variant="danger" className={styles.alert}>
          {errorDisplay.map((e) => (
            <div key={e}>{e}</div>
          ))}
        </Alert>
      )}
      {isSuccess && (
        <Alert variant="success" className={styles.alert}>
          Thanks for the feedback!
        </Alert>
      )}
      <div className={styles.selectPanel}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className={styles.label}>
          If you have a moment, please let us know why you unsubscribed:
          <SelectPanel<UnsubscribeReasonOption>
            getKey={(option) => option?.value ?? ""}
            options={unsubscribeReasonOptions(displayName)}
            renderOption={(option, { isActive }) => (
              <SelectButton.DefinitionContent
                align="center"
                status={<div />}
                isActive={isActive}
                label={option.label}
              />
            )}
            value={reason}
            onClick={setReason}
            disabled={isSuccess}
          />
        </label>
      </div>
      {isReasonOther && (
        <TextArea
          ref={reasonOtherRef}
          label="I am unsubscribing because..."
          value={reasonOther || ""}
          onChange={setReasonOther}
          className={styles.reasonOther}
          disabled={isSuccess}
          errors={error?.errors?.reason_other}
        />
      )}
      <Button type="submit" disabled={!canSubmit} isLoading={isUpdating}>
        Submit
      </Button>
    </Form>
  );
};

export default UnsubscribeReason;
